import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Users, BookOpen, Calendar, PoundSterling, TrendingUp, Plus, BarChart3, Mail, Clock, Info, Pencil, Copy, Crown } from 'lucide-react';
import { Input } from '../ui/input';
import { formatPrice, DAYS_OF_WEEK } from '../../lib/utils';
import { formatGBP, TAX_TOOLTIPS } from '../../lib/vatTaxUtils';
import { RecentTrialEnquiriesPanel } from './RecentTrialEnquiriesPanel';

const OverviewTab = ({
  stats,
  taxBreakdown,
  topClients,
  loadingTopClients,
  pendingEmails,
  editingExpenses,
  setEditingExpenses,
  expensesInput,
  setExpensesInput,
  savingExpenses,
  handleEditExpenses,
  handleSaveExpenses,
  copyTaxBreakdown,
  sendingEmail,
  handleSendEmail,
  setClassModalOpen,
  setScheduleModalOpen,
  setRoomModalOpen,
  setTermModalOpen,
  navigate
}) => {
  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Recent Free Trial Enquiries */}
      <RecentTrialEnquiriesPanel />

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="bg-[#111] border-[#333] hover:border-[#FF007F]/50 transition-colors cursor-pointer"
          onClick={() => navigate('/admin/students')}
          data-testid="total-students-card"
        >
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-white/60">Total Students</p>
                <p className="text-3xl font-bold text-[#FF007F]" data-testid="total-students">
                  {stats?.total_students || 0}
                </p>
              </div>
              <Users className="w-8 h-8 text-[#FF007F]/30" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-[#7000FF]/50 transition-colors cursor-pointer"
          onClick={() => navigate('/admin/classes')}
          data-testid="active-classes-card"
        >
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-white/60">Active Classes</p>
                <p className="text-3xl font-bold text-[#7000FF]" data-testid="active-classes">
                  {stats?.total_classes || 0}
                </p>
              </div>
              <BookOpen className="w-8 h-8 text-[#7000FF]/30" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-emerald-500/50 transition-colors cursor-pointer"
          onClick={() => navigate('/admin/bookings')}
          data-testid="total-bookings-card"
        >
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-white/60">Enrolled Students</p>
                <p className="text-3xl font-bold text-emerald-400" data-testid="total-bookings">
                  {stats?.total_bookings || 0}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-emerald-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-yellow-500/50 transition-colors cursor-pointer"
          onClick={() => navigate('/admin/finance')}
          data-testid="revenue-card"
        >
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-white/60">Revenue</p>
                <p className="text-3xl font-bold text-yellow-400" data-testid="revenue">
                  {formatPrice(stats?.total_revenue || 0)}
                </p>
              </div>
              <PoundSterling className="w-8 h-8 text-yellow-400/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className="bg-[#111] border-[#333] hover:border-[#FF007F]/50 transition-colors cursor-pointer group"
          onClick={() => setClassModalOpen(true)}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-[#FF007F]/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-[#FF007F]/30">
              <Plus className="w-6 h-6 text-[#FF007F]" />
            </div>
            <p className="font-medium text-white">Add Class</p>
            <p className="text-xs text-white/50">Create new class</p>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-[#7000FF]/50 transition-colors cursor-pointer group"
          onClick={() => setScheduleModalOpen(true)}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-[#7000FF]/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-[#7000FF]/30">
              <Calendar className="w-6 h-6 text-[#7000FF]" />
            </div>
            <p className="font-medium text-white">Add Schedule</p>
            <p className="text-xs text-white/50">Set class times</p>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-emerald-500/50 transition-colors cursor-pointer group"
          onClick={() => setRoomModalOpen(true)}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-emerald-500/30">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="font-medium text-white">Add Room</p>
            <p className="text-xs text-white/50">Create venue</p>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#111] border-[#333] hover:border-yellow-500/50 transition-colors cursor-pointer group"
          onClick={() => setTermModalOpen(true)}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-yellow-500/30">
              <Calendar className="w-6 h-6 text-yellow-400" />
            </div>
            <p className="font-medium text-white">Add Term</p>
            <p className="text-xs text-white/50">New semester</p>
          </CardContent>
        </Card>

        <Card
          className="bg-[#111] border-[#333] hover:border-sky-500/50 transition-colors cursor-pointer group"
          onClick={() => navigate('/admin/mailing-lists')}
          data-testid="mailing-lists-quick-action"
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-sky-500/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-sky-500/30">
              <Mail className="w-6 h-6 text-sky-400" />
            </div>
            <p className="font-medium text-white">Mailing Lists</p>
            <p className="text-xs text-white/50">Email parents by group</p>
          </CardContent>
        </Card>
      </div>

      {/* Tax Summary Card */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-yellow-400">
              <PoundSterling className="w-5 h-5" />
              UK VAT & Corporation Tax Summary
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={copyTaxBreakdown}
              className="border-[#333] text-white/70 hover:text-white hover:border-white/50"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy breakdown
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* VAT Section */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">VAT Section</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-3 bg-[#FF007F]/10 rounded-lg border border-[#FF007F]/30">
                  <span className="text-[#FF007F] flex items-center gap-2">
                    Total Sales (VAT inclusive)
                    <span className="cursor-help text-[#FF007F]/50 hover:text-[#FF007F]" title={TAX_TOOLTIPS.totalSales}>ⓘ</span>
                  </span>
                  <span className="font-mono text-lg text-[#FF007F]" data-testid="total-sales">
                    {formatGBP(taxBreakdown.totalSales)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-[#1a1a1a] rounded-lg border border-[#333] ml-4">
                  <span className="text-orange-400 flex items-center gap-2">
                    VAT collected (20% extracted)
                    <span className="cursor-help text-orange-400/50 hover:text-orange-400" title={TAX_TOOLTIPS.vatCollected}>ⓘ</span>
                  </span>
                  <span className="font-mono text-lg text-orange-400" data-testid="vat-collected">
                    {formatGBP(taxBreakdown.vatCollected)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <span className="text-white/70 flex items-center gap-2">
                    Net Turnover (ex VAT)
                    <span className="cursor-help text-white/30 hover:text-white/70" title={TAX_TOOLTIPS.netTurnover}>ⓘ</span>
                  </span>
                  <span className="font-mono text-lg text-white" data-testid="net-turnover">
                    {formatGBP(taxBreakdown.netTurnover)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Profit & Tax Section */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">Profit & Corporation Tax</h4>
              <div className="space-y-2">
                {/* Expenses - Editable */}
                <div className="flex justify-between items-center p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <span className="text-orange-400 flex items-center gap-2">
                    Allowable Expenses
                    <span className="cursor-help text-orange-400/50 hover:text-orange-400" title={TAX_TOOLTIPS.expenses}>ⓘ</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {editingExpenses ? (
                      <>
                        <div className="flex items-center">
                          <span className="text-orange-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={expensesInput}
                            onChange={(e) => setExpensesInput(e.target.value)}
                            className="w-28 h-8 bg-[#111] border-orange-500/50 text-orange-400 font-mono text-right"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveExpenses();
                              if (e.key === 'Escape') setEditingExpenses(false);
                            }}
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={handleSaveExpenses}
                          disabled={savingExpenses}
                          className="h-8 px-2 bg-green-600 hover:bg-green-700"
                        >
                          {savingExpenses ? '...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingExpenses(false)}
                          className="h-8 px-2 text-white/50 hover:text-white"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="font-mono text-lg text-orange-400" data-testid="expenses">
                          −{formatGBP(stats?.total_expenses ?? 0)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleEditExpenses}
                          className="h-7 w-7 p-0 text-orange-400/50 hover:text-orange-400 hover:bg-orange-500/10"
                          title="Edit expenses"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Net Profit */}
                <div className="flex justify-between items-center p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <span className="text-white/70 flex items-center gap-2">
                    Net Profit (before tax)
                    <span className="cursor-help text-white/30 hover:text-white/70" title={TAX_TOOLTIPS.profit}>ⓘ</span>
                  </span>
                  <span className={`font-mono text-lg ${taxBreakdown.hasLoss ? 'text-red-400' : 'text-white'}`} data-testid="net-profit">
                    {formatGBP(taxBreakdown.netProfit)}
                  </span>
                </div>
                
                {/* Corporation Tax */}
                <div className="flex justify-between items-center p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <span className="text-purple-400 flex items-center gap-2">
                    Corporation Tax due
                    <span className="cursor-help text-purple-400/50 hover:text-purple-400" title={TAX_TOOLTIPS.corporationTax}>ⓘ</span>
                    {taxBreakdown.taxBand === 'marginal' && (
                      <Badge className="bg-purple-500/20 text-purple-300 text-xs border-purple-500/30">Marginal Relief</Badge>
                    )}
                    {taxBreakdown.taxBand === 'small' && (
                      <Badge className="bg-green-500/20 text-green-300 text-xs border-green-500/30">Small Profits Rate</Badge>
                    )}
                  </span>
                  <span className="font-mono text-lg text-purple-400" data-testid="corporation-tax">
                    {formatGBP(taxBreakdown.corporationTax)}
                  </span>
                </div>

                {/* Take-Home */}
                <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="text-emerald-400 font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Take-Home Profit
                    <span className="cursor-help text-emerald-400/50 hover:text-emerald-400" title={TAX_TOOLTIPS.takeHome}>ⓘ</span>
                  </span>
                  <span className="font-mono text-xl font-bold text-emerald-400" data-testid="take-home">
                    {formatGBP(taxBreakdown.takeHome)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Clients Card */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-400">
            <Crown className="w-5 h-5" />
            Top Clients by Spending
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTopClients ? (
            <div className="py-8 text-center text-white/50">Loading...</div>
          ) : topClients.length === 0 ? (
            <div className="py-8 text-center text-white/50">No client data yet</div>
          ) : (
            <div className="space-y-2">
              {topClients.slice(0, 5).map((client, index) => (
                <div key={client.user_id || index} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-[#333] text-white/50'
                    }`}>
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-white">{client.name || 'Unknown'}</p>
                      <p className="text-xs text-white/50">{client.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-emerald-400">{formatPrice(client.total_spent)}</p>
                    <p className="text-xs text-white/50">{client.booking_count} bookings</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Emails Card */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-400">
            <Mail className="w-5 h-5" />
            Pending Email Automations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Reminders */}
            <div>
              <h4 className="text-sm font-semibold text-white/70 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Payment Reminders
              </h4>
              {pendingEmails.reminders?.length === 0 ? (
                <p className="text-sm text-white/50 py-2">No pending reminders</p>
              ) : (
                <div className="space-y-2">
                  {pendingEmails.reminders?.slice(0, 3).map((email, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#1a1a1a] rounded border border-[#333]">
                      <div>
                        <p className="text-sm text-white">{email.parent_name}</p>
                        <p className="text-xs text-white/50">{email.email}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSendEmail('reminder', email)}
                        disabled={sendingEmail === `reminder-${email.booking_id}`}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {sendingEmail === `reminder-${email.booking_id}` ? '...' : 'Send'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
